import React, { useState, useEffect, useContext } from 'react';
import {
  FaEnvelope,
  FaClock,
  FaQuestionCircle,
  FaBug,
  FaLightbulb,
  FaHandshake,
  FaPaperPlane,
  FaCheckCircle
} from 'react-icons/fa';
import SEO from '../components/SEO';
import { showToast } from '../components/ToastNotification';
import { AuthContext } from '../contexts/AuthContext';
import analytics from '../services/analytics';
import { sendContactEmail } from '../services/emailService';

const Contact = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prefill form with user data if logged in
  useEffect(() => {
    if (isAuthenticated) {
      const userData = JSON.parse(localStorage.getItem("user_data") || sessionStorage.getItem("user_data") || "{}");
      if (userData.firstName || userData.email) {
        setFormData(prev => ({
          ...prev,
          name: userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.firstName || '',
          email: userData.email || ''
        }));
      }
    }
  }, [isAuthenticated]);

  const categories = [
    { value: 'support', label: 'Technical Support', icon: <FaQuestionCircle className="w-5 h-5" /> },
    { value: 'bug', label: 'Bug Report', icon: <FaBug className="w-5 h-5" /> },
    { value: 'feature', label: 'Feature Request', icon: <FaLightbulb className="w-5 h-5" /> },
    { value: 'partnership', label: 'Partnership Inquiry', icon: <FaHandshake className="w-5 h-5" /> },
    { value: 'other', label: 'Other', icon: <FaEnvelope className="w-5 h-5" /> }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      showToast('Please fill in all required fields', 'error');
      analytics.trackFormSubmit('contact_page', false);
      return;
    }

    setIsSubmitting(true);

    // Track form submission attempt
    const categoryLabel = formData.category ? categories.find(c => c.value === formData.category)?.label : 'General';
    analytics.event('Contact', 'form_submitted', 'contact_page', null, {
      category: categoryLabel,
      has_subject: !!formData.subject,
      message_length: formData.message.length
    });

    try {
      // Send email via API
      await sendContactEmail(formData, 'contact', 'support@riskdiscover.com');

      showToast('Message sent successfully! We\'ll get back to you within 24 hours.', 'success');

      // Track successful form submission
      analytics.trackFormSubmit('contact_page', true);
      analytics.event('Contact', 'email_sent_success', categoryLabel);

      // Reset form (but keep user data if authenticated)
      if (isAuthenticated) {
        const userData = JSON.parse(localStorage.getItem("user_data") || sessionStorage.getItem("user_data") || "{}");
        setFormData({
          name: userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.firstName || '',
          email: userData.email || '',
          subject: '',
          category: '',
          message: ''
        });
      } else {
        setFormData({
          name: '',
          email: '',
          subject: '',
          category: '',
          message: ''
        });
      }

    } catch (error) {
      console.error('Error sending message:', error);
      showToast('Failed to send message. Please try again or email us directly at support@riskdiscover.com', 'error');
      analytics.trackFormSubmit('contact_page', false);
      analytics.event('Contact', 'email_send_failed', categoryLabel);
    } finally {
      setIsSubmitting(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does a risk assessment take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most assessments complete in under 2 minutes. Complex analyses may take slightly longer."
        }
      },
      {
        "@type": "Question",
        "name": "Can I download my assessment reports?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! All reports can be exported as professional PDF documents for presentations and records."
        }
      },
      {
        "@type": "Question",
        "name": "How many assessments can I run?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "This depends on your plan. Basic plan includes annual credits that refresh yearly with your subscription."
        }
      },
      {
        "@type": "Question",
        "name": "Is my data secure?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. We use enterprise-grade security and never share your assessment data with third parties."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEO 
        title="Contact RiskDiscover | Get Expert Risk Assessment Support"
        description="Need help with risk assessment? Contact our expert team for technical support, demos, and partnership inquiries. We're here to help."
        canonical="/contact"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get in Touch
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Have questions about our risk assessment platform? We're here to help you succeed.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        
        {/* FAQ Section */}
        <div className="mb-16">
          <div className="bg-gray-50 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How long does a risk assessment take?</h3>
                <p className="text-gray-600 text-sm">
                  Most assessments complete in under 2 minutes. Complex analyses may take slightly longer.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Can I download my assessment reports?</h3>
                <p className="text-gray-600 text-sm">
                  Yes! All reports can be exported as professional PDF documents for presentations and records.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How many assessments can I run?</h3>
                <p className="text-gray-600 text-sm">
                  This depends on your plan. Basic plan includes annual credits that refresh yearly with your subscription.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Is my data secure?</h3>
                <p className="text-gray-600 text-sm">
                  Absolutely. We use enterprise-grade security and never share your assessment data with third parties.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your.email@company.com"
                    required
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {categories.map((category) => (
                    <label
                      key={category.value}
                      className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.category === category.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="category"
                        value={category.value}
                        checked={formData.category === category.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      {category.icon}
                      <span className="text-sm font-medium">{category.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of your inquiry"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Please provide as much detail as possible about your question or issue..."
                  required
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4 text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-8 py-3 rounded-full font-semibold flex items-center justify-center space-x-2 transition-all hover:shadow-xl transform hover:scale-105 disabled:transform-none mx-auto"
                >
                  <FaPaperPlane className="w-4 h-4" />
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  We'll respond within 24 hours
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Contact Information</h2>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="flex flex-col items-center">
                <div className="p-4 bg-blue-100 rounded-full mb-4">
                  <FaEnvelope className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email Support</h3>
                <p className="text-gray-600 font-medium">support@riskdiscover.com</p>
                <p className="text-sm text-gray-500 mt-1">
                  For all inquiries and technical support
                </p>
              </div>

              <div className="flex flex-col items-center">
                <div className="p-4 bg-green-100 rounded-full mb-4">
                  <FaClock className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Response Time</h3>
                <p className="text-gray-600 font-medium">Within 3 business days</p>
                <p className="text-sm text-gray-500 mt-1">
                  We typically respond much faster
                </p>
              </div>

              <div className="flex flex-col items-center">
                <div className="p-4 bg-purple-100 rounded-full mb-4">
                  <FaCheckCircle className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">What to Expect</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Personalized response from our team</li>
                  <li>• Technical guidance and solutions</li>
                  <li>• Follow-up until issue is resolved</li>
                </ul>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Help</h3>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="/features" onClick={() => analytics.trackButtonClick('features_link', 'contact')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → View all features
                </a>
                <a href="/products" onClick={() => analytics.trackButtonClick('products_link', 'contact')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → Compare plans
                </a>
                <a href="/dashboard" onClick={() => analytics.trackButtonClick('dashboard_link', 'contact')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → Access your dashboard
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;